// Runtime env + app config in one module so `env` is never an unresolved import
// (avoids "Cannot read properties of undefined (reading 'API_URL')" with chunk ordering in prod).
// Build HTTP URLs with `api` / `reporting` — do not concatenate env.API_URL in pages.
import { createApiUrls, createReportingUrls, resolveApiOrigin } from "./api-url.js";

const viteEnv =
  typeof import.meta !== "undefined" && import.meta.env ? import.meta.env : {};

const runtime =
  typeof window !== "undefined" && window.__SGRC_RUNTIME__
    ? window.__SGRC_RUNTIME__
    : {};

/**
 * Runtime config wins when the key is a string (including empty string for same-origin / Faz C).
 * Falls back to Vite build-time env, then caller default.
 */
function pickUrl(runtimeKey, viteValue) {
  if (typeof runtime[runtimeKey] === "string") return runtime[runtimeKey].trim();
  if (typeof viteValue === "string") return viteValue.trim();
  return "";
}

const apiUrl = pickUrl("apiUrl", viteEnv.VITE_API_URL);
const wsUrl = pickUrl("wsUrl", viteEnv.VITE_WS_URL);
const reportingUrl = pickUrl(
  "reportingUrl",
  viteEnv.VITE_REPORTING_SERVER_URL || viteEnv.VITE_REPORTING_URL,
);

const isBrowser = typeof window !== "undefined";
const sameOrigin = isBrowser ? window.location.origin : "http://localhost:3001";

export const env = {
  API_URL: apiUrl || sameOrigin,
  REPORTING_SERVER_URL: reportingUrl || sameOrigin,
  /* Prefer same origin as API (http(s)://host:port); ws:// URLs also work for socket.io */
  WS_URL: wsUrl || apiUrl || sameOrigin,
  /**
   * Socket.IO on unless VITE_WS_ENABLED=false.
   * My Work list sync (`my_work_invalidated`) needs a live socket in local `npm run dev`.
   */
  WS_ENABLED: viteEnv.VITE_WS_ENABLED !== "false",
  DEPLOY_MODE: runtime.deployMode || "split",
};

/** REST + origin URLs. Pages use `api.url` / `api.resource`; SDK uses `api.origin`. */
export const api = createApiUrls({ origin: env.API_URL || sameOrigin });

/** DevExpress reporting host. Pages use `reporting.origin` / `reporting.url`. */
export const reporting = createReportingUrls({ origin: env.REPORTING_SERVER_URL });

/**
 * Socket.IO server URL: same host as API, but never strip "/api" from the hostname
 * (naive string replace turns https://api.example.com into invalid URLs).
 */
export function getSocketIoServerUrl() {
  const raw = env.WS_URL;
  if (!raw || typeof raw !== "string") return sameOrigin;
  const trimmed = raw.trim();
  if (!trimmed) return sameOrigin;
  return resolveApiOrigin(trimmed) || sameOrigin;
}

export const config = {
  apiUrl: env.API_URL,
  reportingServerUrl: env.REPORTING_SERVER_URL,
  wsUrl: env.WS_URL,
  ws: {
    enabled: env.WS_ENABLED,
    reconnectInitialMs: 1000,
    reconnectMaxMs: 10000,
  },
};
